import { createContext, useEffect, useMemo, useRef, useState } from 'react';
import PhotoSwipe from 'photoswipe';
import PhotoSwipeUIDefault from 'photoswipe/dist/photoswipe-ui-default';
import { useRouter } from 'next/router';

import PhotoSwipeGallery from 'components/photoswipegallery';

export const PhotoSwipeContext = createContext(null);

export default function PhotoSwipeProvider({ children, gallery }) {
  const [node, setNode] = useState(null);
  const router = useRouter();
  const pswp = useRef(null);

  const items = useMemo(
    () =>
      gallery.photos.map((photo) => ({
        id: photo.id,
        src: `/_next/image?url=${encodeURIComponent(photo.url)}&w=1200&q=75`,
        w: photo.width,
        h: photo.height,
      })),
    [gallery]
  );

  // Open PhotoSwipe if photo ID was provided
  useEffect(() => {
    if (node === null) {
      return;
    }

    // If there is an image in the querystring
    if (router.query.photo?.length === 1) {
      const index = items.findIndex((i) => i.id === router.query.photo[0]);
      if (pswp.current === null) {
        // Gallery is closed, open it
        pswp.current = new PhotoSwipe(node, PhotoSwipeUIDefault, items, {
          index,
          history: false,
        });
        pswp.current.init();

        // Update image URL on navigation
        pswp.current.listen('afterChange', () =>
          router.replace(
            `/${gallery.id}/${pswp.current.currItem.id}`,
            undefined,
            {
              shallow: true,
            }
          )
        );

        // Set gallery URL on close
        pswp.current.listen('close', () => {
          router.replace(`/${gallery.id}`, undefined, { shallow: true });
          pswp.current = null;
        });
      } else if (pswp.current.currItem === index) {
        // Gallery is open, switch to this slide
        pswp.current.goto(index);
      }
    } else {
      // No image in the query string, close the gallery if it is open
      if (pswp.current !== null) {
        pswp.current.close();
      }
    }
  }, [gallery, items, router.query, node]);

  return (
    <PhotoSwipeContext.Provider
      value={{
        node,
        setNode,
        gallery,
      }}
    >
      {children}
      <PhotoSwipeGallery />
    </PhotoSwipeContext.Provider>
  );
}
